import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Edit, Eye, Plus, ChevronDown, Tag, Package, Users, XCircle, Mail, Calendar as CalendarIcon, Grid3x3, MapPin, Shield } from 'lucide-react';
import { fairManagerApi } from '../services/api';
import { Fair } from '../types';
import FairCancellationModal from './FairCancellationModal';
import { extractApiError } from '../utils/errorUtils';
import { parseDateOnly } from '../utils/fairStatus'; // Issue #1672

export default function FairManagerFairs() {
  const navigate = useNavigate();
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [fairToCancel, setFairToCancel] = useState<{id: number, name: string} | null>(null);

  useEffect(() => {
    loadFairs();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown !== null) {
        const ref = dropdownRefs.current.get(openDropdown);
        const target = event.target as Node;

        // Don't close if clicking a button inside the dropdown
        if (ref && !ref.contains(target)) {
          setOpenDropdown(null);
        }
      }
    };

    // Use 'click' instead of 'mousedown' to allow button clicks to fire first
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && openDropdown !== null) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openDropdown]);

  const loadFairs = async () => {
    try {
      setLoading(true);
      const data = await fairManagerApi.getFairs();
      setFairs(data);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load managed fairs:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  // Format next date for display (Issue #82, #1672)
  const formatNextDate = (nextDate: string | null | undefined): string => {
    const date = parseDateOnly(nextDate); // Issue #1672: handles YYYY-MM-DD as local time
    if (!date) return 'TBD';

    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Past';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays <= 7) return `${diffDays} days`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="text-center py-8" data-testid="fairs-loading">
        <p className="text-gray-500">Loading your fairs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8" data-testid="fairs-error">
        <p className="text-red-500" data-testid="fairs-error-message">{error}</p>
        <button
          onClick={loadFairs}
          className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
          data-testid="fairs-page-try-again-button"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 w-full max-w-full">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-3">
        <div className="flex flex-col gap-2">
          <h1 className="text-base sm:text-xl font-bold text-gray-900" data-testid="fairs-heading">My Fairs</h1>
          <Link
            to="/fairmanager/fair/new"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm"
            data-testid="create-fair-button"
          >
            <Plus className="h-4 w-4 flex-shrink-0" />
            <span>Create New Fair</span>
          </Link>
        </div>
      </div>

      {fairs.length === 0 ? (
        <div className="bg-white shadow-sm rounded-lg p-8 text-center" data-testid="fairs-empty-state">
          <p className="text-gray-500 mb-4" data-testid="fairs-empty-message">You haven't created any fairs yet.</p>
          <Link
            to="/fairmanager/fair/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
            data-testid="create-first-fair-button"
          >
            <Plus className="h-4 w-4" />
            Create Your First Fair
          </Link>
        </div>
      ) : (
        <div data-testid="fairs-grid">
          {/* Desktop Table View (hidden on mobile) */}
          <div className="hidden lg:block bg-white shadow-sm rounded-lg overflow-visible">
            <div>
              <table className="w-full table-fixed divide-y divide-gray-200" data-testid="fairs-list">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '30%'}}>
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '15%'}}>
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '12%'}}>
                      Next Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                      Slots
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                      Vendors
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '13%'}}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fairs.map((fair) => (
                    <tr key={fair.id} data-testid={`fair-card-${fair.id}`} data-fair-id={fair.id}>
                      <td className="px-4 py-4">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate" data-testid="fair-card-name">
                            {fair.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {fair.description ? fair.description.substring(0, 50) + '...' : 'No description'}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 truncate">
                          {fair.address.city}, {fair.address.state}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900">
                          {formatNextDate(fair.next_date)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900">
                          {fair.available_slots !== undefined && fair.total_slots ? (
                            <span>{fair.available_slots} / {fair.total_slots}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900">
                          {fair.vendor_count !== undefined ? (
                            <span>{fair.vendor_count}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {fair.is_draft ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Draft
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Published
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/fair/${fair.id}`}
                            className="text-secondary hover:text-teal-900"
                            title="View Fair"
                            data-testid="view-fair-link"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <Link
                            to={`/fairmanager/fairs/${fair.id}/edit`}
                            className="text-secondary hover:text-ink"
                            title="Edit Fair"
                            data-testid="edit-fair-link"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>

                          {/* Manage Dropdown */}
                          <div className="relative" ref={(el) => el && dropdownRefs.current.set(fair.id, el)}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdown(openDropdown === fair.id ? null : fair.id);
                              }}
                              className="flex items-center gap-1 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
                              title="Manage Fair"
                              aria-expanded={openDropdown === fair.id}
                              aria-haspopup="true"
                              data-testid="manage-fair-button"
                            >
                              Manage
                              <ChevronDown className="h-3 w-3" />
                            </button>

                            {openDropdown === fair.id && (
                              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200" role="menu" data-testid="manage-fair-dropdown">
                                <button
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  role="menuitem"
                                  data-testid="pricing-templates-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(null);
                                    navigate(`/fairmanager/fair/${fair.id}/pricing`);
                                  }}
                                >
                                  <Tag className="h-4 w-4" />
                                  Pricing Templates
                                </button>
                                <button
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  role="menuitem"
                                  data-testid="rental-equipment-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(null);
                                    navigate(`/fairmanager/fair/${fair.id}/rentals`);
                                  }}
                                >
                                  <Package className="h-4 w-4" />
                                  Rental Equipment
                                </button>
                                <button
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  role="menuitem"
                                  data-testid="waitlist-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(null);
                                    navigate(`/fairmanager/fair/${fair.id}/waitlist`);
                                  }}
                                >
                                  <Users className="h-4 w-4" />
                                  Waitlist
                                </button>
                                <button
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  role="menuitem"
                                  data-testid="email-campaigns-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(null);
                                    navigate(`/fairmanager/fairs/${fair.id}/email-campaigns`);
                                  }}
                                >
                                  <Mail className="h-4 w-4" />
                                  Email Campaigns
                                </button>
                                <button
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  role="menuitem"
                                  data-testid="availability-matrix-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(null);
                                    navigate(`/fairmanager/fairs/${fair.id}/availability`);
                                  }}
                                >
                                  <CalendarIcon className="h-4 w-4" />
                                  Availability Matrix
                                </button>
                                <button
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  role="menuitem"
                                  data-testid="booth-layouts-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(null);
                                    navigate(`/fairmanager/fairs/${fair.id}/booth-layouts`);
                                  }}
                                >
                                  <Grid3x3 className="h-4 w-4" />
                                  Booth Layouts
                                </button>
                                <button
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  role="menuitem"
                                  data-testid="amenities-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(null);
                                    navigate(`/fairmanager/fairs/${fair.id}/amenities`);
                                  }}
                                >
                                  <MapPin className="h-4 w-4" />
                                  Booth Amenities
                                </button>
                                <button
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  role="menuitem"
                                  data-testid="contact-settings-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(null);
                                    navigate(`/fairmanager/fairs/${fair.id}/contact-settings`);
                                  }}
                                >
                                  <Shield className="h-4 w-4" />
                                  Contact Settings
                                </button>
                                <button
                                  onClick={() => {
                                    setFairToCancel({id: fair.id, name: fair.name});
                                    setCancelModalOpen(true);
                                    setOpenDropdown(null);
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-red-700 hover:bg-red-50 w-full text-left"
                                  role="menuitem"
                                  data-testid="cancel-fair-button"
                                >
                                  <XCircle className="h-4 w-4" />
                                  Cancel Fair
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View (visible on mobile only) */}
          <div className="lg:hidden space-y-3" data-testid="fairs-list">
            {fairs.map((fair) => (
              <div key={fair.id} className="bg-white shadow-sm rounded-lg p-4" data-testid={`fair-card-${fair.id}`} data-fair-id={fair.id}>
                <div className="flex items-start gap-3 mb-3">
                  <img
                    className="h-12 w-12 rounded-full object-cover flex-shrink-0"
                    src={fair.image}
                    alt={fair.name}
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate" data-testid="fair-card-name">
                      {fair.name}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">
                      {fair.address.city}, {fair.address.state}
                    </p>
                  </div>
                  {fair.is_draft ? (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 flex-shrink-0">
                      Draft
                    </span>
                  ) : (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex-shrink-0">
                      Published
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                  <div>
                    <div className="text-gray-500">Next Date</div>
                    <div className="font-medium text-gray-900">
                      {formatNextDate(fair.next_date)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Slots</div>
                    <div className="font-medium text-gray-900">
                      {fair.available_slots !== undefined && fair.total_slots ? (
                        <span>{fair.available_slots} / {fair.total_slots}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Vendors</div>
                    <div className="font-medium text-gray-900">
                      {fair.vendor_count !== undefined ? (
                        <span>{fair.vendor_count}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link
                    to={`/fair/${fair.id}`}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    data-testid="view-fair-link"
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </Link>
                  <Link
                    to={`/fairmanager/fairs/${fair.id}/edit`}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark"
                    data-testid="edit-fair-link"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Link>
                  {/* Manage Dropdown for Mobile */}
                  <div className="relative flex-1" ref={(el) => el && dropdownRefs.current.set(fair.id, el)}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(openDropdown === fair.id ? null : fair.id);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      aria-expanded={openDropdown === fair.id}
                      aria-haspopup="true"
                      data-testid="manage-fair-button"
                    >
                      Manage
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    {openDropdown === fair.id && (
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200" role="menu" data-testid="manage-fair-dropdown">
                        <button
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                          role="menuitem"
                          data-testid="pricing-templates-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(null);
                            navigate(`/fairmanager/fair/${fair.id}/pricing`);
                          }}
                        >
                          <Tag className="h-4 w-4" />
                          Pricing
                        </button>
                        <button
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                          role="menuitem"
                          data-testid="rental-equipment-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(null);
                            navigate(`/fairmanager/fair/${fair.id}/rentals`);
                          }}
                        >
                          <Package className="h-4 w-4" />
                          Rentals
                        </button>
                        <button
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                          role="menuitem"
                          data-testid="waitlist-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(null);
                            navigate(`/fairmanager/fair/${fair.id}/waitlist`);
                          }}
                        >
                          <Users className="h-4 w-4" />
                          Waitlist
                        </button>
                        <button
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                          role="menuitem"
                          data-testid="email-campaigns-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(null);
                            navigate(`/fairmanager/fairs/${fair.id}/email-campaigns`);
                          }}
                        >
                          <Mail className="h-4 w-4" />
                          Email
                        </button>
                        <button
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                          role="menuitem"
                          data-testid="availability-matrix-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(null);
                            navigate(`/fairmanager/fairs/${fair.id}/availability`);
                          }}
                        >
                          <CalendarIcon className="h-4 w-4" />
                          Availability
                        </button>
                        <button
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                          role="menuitem"
                          data-testid="booth-layouts-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(null);
                            navigate(`/fairmanager/fairs/${fair.id}/booth-layouts`);
                          }}
                        >
                          <Grid3x3 className="h-4 w-4" />
                          Layouts
                        </button>
                        <button
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                          role="menuitem"
                          data-testid="amenities-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(null);
                            navigate(`/fairmanager/fairs/${fair.id}/amenities`);
                          }}
                        >
                          <MapPin className="h-4 w-4" />
                          Amenities
                        </button>
                        <button
                          onClick={() => {
                            setFairToCancel({id: fair.id, name: fair.name});
                            setCancelModalOpen(true);
                            setOpenDropdown(null);
                          }}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-red-700 hover:bg-red-50 w-full text-left"
                          role="menuitem"
                          data-testid="cancel-fair-button"
                        >
                          <XCircle className="h-4 w-4" />
                          Cancel Fair
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fair Cancellation Modal */}
      {fairToCancel && (
        <FairCancellationModal
          isOpen={cancelModalOpen}
          onClose={() => {
            setCancelModalOpen(false);
            setFairToCancel(null);
          }}
          onSuccess={() => {
            loadFairs();
          }}
          fair={fairToCancel}
        />
      )}
    </div>
  );
}
